import React from 'react';

import cn from 'classnames';
import clsx from 'classnames';
import { useTranslation } from 'react-i18next';

import { IJournalEvent } from '@entities/eventJournal';
import { useIsOverflowingContent } from '@entities/eventJournal/lib/hooks';
import { getPlainTextWithSpacesFromHtml } from '@shared/helpers';
import { htmlParser } from '@shared/helpers/htmlParser';
import { UiText, useLightBoxControl } from '@shared/ui';

import cls from './TextEventJournalItem.module.css';
import { EventJournalAuthor } from '../EventJournalAuthor/EventJournalAuthor';

interface EventJournalItemProps {
    estimateName: string;
    classes?: string;
    textEvent: IJournalEvent;
    onClick?: () => void;
    isExport?: boolean;
}

export const TextEventJournalItem = ({
    classes,
    estimateName,
    textEvent,
    onClick,
    isExport,
}: EventJournalItemProps) => {
    const { onShowLightBox } = useLightBoxControl();
    const { descriptionRef, estimateRef, titleAttrDescriptionText, titleAttrEstimateText } =
        useIsOverflowingContent({
            descriptionText: textEvent?.description ?? '',
            estimateText: estimateName,
        });

    const { t } = useTranslation();

    const files = textEvent?.attachments?.map((file) => ({
        id: file.id,
        fileSrc: file.originalUrl,
        mimeType: file.mimeType,
        fileName: file.name,
        description: getPlainTextWithSpacesFromHtml(textEvent.description),
        author: textEvent.author,
    }));

    return (
        <div
            className={cn(cls.EventJournalItem, [classes])}
            onClick={onClick && onClick}
            data-type={textEvent.type}
        >
            <div className={cls.EventJournalItemBody}>
                <div
                    title={titleAttrDescriptionText}
                    ref={descriptionRef}
                    className={cn(cls.description)}
                >
                    <div className={cls.EventJournalItemIcon}>
                        <img src={textEvent.iconUrl} alt="textEvent icon" />
                    </div>
                    <div
                        className={cn({
                            [cls.descriptionWrapperExport]: isExport && textEvent.type === 'custom',
                        })}
                    >
                        <UiText
                            classes={
                                textEvent.type === 'custom'
                                    ? cls.customDescriptionContent
                                    : cls.descriptionContent
                            }
                            variant="mediumPlain"
                            tag="span"
                        >
                            {htmlParser(textEvent.description)}
                        </UiText>
                    </div>
                </div>
                {!!textEvent?.attachments?.length && !isExport && (
                    <div className={cls.filesWrapper}>
                        {textEvent?.attachments?.map((attachment) => (
                            <div
                                key={attachment.id}
                                className={cls.attachmentItem}
                                onClick={() =>
                                    onShowLightBox({
                                        id: attachment.id,
                                        newFiles: files,
                                    })
                                }
                                title={attachment?.name}
                            >
                                <img src={attachment.previewSvgUrl} alt={attachment.name} />
                            </div>
                        ))}
                    </div>
                )}
                <div className={cls.footer}>
                    <EventJournalAuthor author={textEvent.author} isExport={isExport} />
                    {textEvent?.estimateId && (
                        <UiText
                            tag="span"
                            variant="caption"
                            className={clsx(!isExport && cls.estimateName)}
                            title={titleAttrEstimateText}
                            ref={estimateRef}
                        >
                            {t('TextEventJournalItem.EventJournalItem.footer.UiText')}:{' '}
                            {estimateName}
                        </UiText>
                    )}
                </div>
            </div>
        </div>
    );
};
